// The Enterprise quote (D109, APS-361): a price from an organization's numbers (or a blank one),
// every figure with how it was computed, saved with the cost model's version. Console D (APS-412):
// the inputs and the prices side by side, the save in its form window under the head.
import { PageHead, Section } from '@apsio/ui'
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { type FormEvent, Fragment, type ReactNode, useEffect, useId, useState } from 'react'
import {
  computeQuote,
  type PlanFit,
  planFitQuery,
  type QuoteComputed,
  type QuoteInput,
  quoteQuery,
  quotesQuery,
  saveQuote,
} from '../api/queries'
import { DataTable, FormWindow, ReasonField } from '../components/kit'
import { Empty, ErrorState, Loading } from '../components/states'
import { type MessageId, useI18n } from '../i18n'
import { useFormat } from '../lib/format'
import { MoneyWarns } from './Margin'

export const QUOTE_DEFAULTS: QuoteInput = {
  sessions_per_month: 1_500_000,
  captured_per_month: 1_000_000,
  growth_per_year: 0.3,
  replay_share: 0.25,
  replay_minutes: 4,
  records_per_session: 200,
  crashes_per1k: 5,
  detail_days: 30,
  crash_days: 395,
  sso: false,
  scim: false,
  security_hours: 0,
  sla: 'none',
  support_hours_per_month: 0,
  onboarding_hours: 0,
  term_years: 1,
  payment: 'invoice',
  currency: 'usd',
  target_margin: 0.7,
  floor_margin: 0.5,
  hourly_rate: 100,
  overhead_share: 0.05,
}

/** An organization's numbers as a quote's inputs (its plan fit). */
export function inputsFrom(p: PlanFit): QuoteInput {
  const last = p.months.at(-1)
  const on = (f: string) => p.flags.some((x) => x.flag === f && x.on)
  const counted = Math.max(p.fit.counted, p.fit.captured)
  return {
    ...QUOTE_DEFAULTS,
    sessions_per_month: Math.round(counted),
    captured_per_month: Math.round(p.fit.captured),
    growth_per_year:
      p.fit.captured > 0
        ? Math.min(10, Math.max(-1, (p.fit.slope * 12) / p.fit.captured))
        : QUOTE_DEFAULTS.growth_per_year,
    replay_share: Math.min(1, p.profile.replay_share),
    replay_minutes: Math.min(60, Math.round(p.profile.replay_minutes * 10) / 10),
    records_per_session: Math.round(p.profile.records_per_session),
    crashes_per1k:
      last && last.counted > 0 ? Math.min(1000, (last.crashes / last.counted) * 1000) : 5,
    detail_days: p.profile.detail_days,
    sso: on('sso'),
    scim: on('scim'),
    sla: on('sla') ? '99.9' : 'none',
    payment: on('invoice') ? 'invoice' : 'card',
    currency: p.facts.currency === 'brl' ? 'brl' : 'usd',
  }
}

type NumberKey = {
  [K in keyof QuoteInput]: QuoteInput[K] extends number ? K : never
}[keyof QuoteInput]

/** The number fields, in the form's groups; `pct` ones are entered as percents. */
const GROUPS: { title: MessageId; fields: { key: NumberKey; pct?: boolean }[] }[] = [
  {
    title: 'quote.group.volume',
    fields: [
      { key: 'sessions_per_month' },
      { key: 'captured_per_month' },
      { key: 'growth_per_year', pct: true },
    ],
  },
  {
    title: 'quote.group.shape',
    fields: [
      { key: 'replay_share', pct: true },
      { key: 'replay_minutes' },
      { key: 'records_per_session' },
      { key: 'crashes_per1k' },
      { key: 'detail_days' },
      { key: 'crash_days' },
    ],
  },
  {
    title: 'quote.group.service',
    fields: [
      { key: 'support_hours_per_month' },
      { key: 'onboarding_hours' },
      { key: 'security_hours' },
      { key: 'hourly_rate' },
    ],
  },
  {
    title: 'quote.group.targets',
    fields: [
      { key: 'target_margin', pct: true },
      { key: 'floor_margin', pct: true },
      { key: 'overhead_share', pct: true },
    ],
  },
]

function Inputs({ value, onChange }: { value: QuoteInput; onChange: (next: QuoteInput) => void }) {
  const { t, number } = useI18n()
  const base = useId()
  // While a field is being typed in, its text as typed; otherwise its value grouped through the
  // locale (Mosaic): 1.500.000, 76,7. Typing reads either form back.
  const [text, setText] = useState<Record<string, string>>({})
  const [group, decimal] = [...number(1111.1).replace(/\d/g, '')]
  const shown = (key: NumberKey, pct?: boolean) =>
    text[key] ??
    number(pct ? +(value[key] * 100).toFixed(2) : value[key], { maximumFractionDigits: 4 })
  const set = (key: NumberKey, raw: string, pct?: boolean) => {
    setText({ ...text, [key]: raw })
    // A group separator only before three digits ("1.500.000"); "4.2" stays a decimal.
    const g = (group ?? ',').replace(/[.*+?^${}()|[\]\\]/g, '\\$&')
    const plain = raw.replace(new RegExp(`${g}(?=\\d{3}(\\D|$))`, 'g'), '')
    const n = Number(plain.replace(decimal ?? '.', '.'))
    if (raw.trim() !== '' && Number.isFinite(n)) onChange({ ...value, [key]: pct ? n / 100 : n })
  }
  const done = (key: NumberKey) => {
    const { [key]: _typed, ...rest } = text
    setText(rest)
  }
  const select = <K extends 'sla' | 'payment' | 'currency' | 'term_years'>(
    key: K,
    options: { v: QuoteInput[K]; label: MessageId }[],
  ) => (
    <label className="lbl" htmlFor={`${base}-${key}`}>
      {t(`quote.field.${key}` as MessageId)}
      <select
        id={`${base}-${key}`}
        className="select"
        value={String(value[key])}
        onChange={(e) => {
          const picked = options.find((o) => String(o.v) === e.target.value)
          if (picked) onChange({ ...value, [key]: picked.v })
        }}
      >
        {options.map((o) => (
          <option key={String(o.v)} value={String(o.v)}>
            {t(o.label)}
          </option>
        ))}
      </select>
    </label>
  )
  return (
    <form
      className="win form"
      aria-label={t('quote.inputs')}
      onSubmit={(e: FormEvent) => e.preventDefault()}
    >
      <div className="win-bar">
        <b>{t('quote.inputs')}</b>
        <span>· {t('quote.inputsMeta')}</span>
      </div>
      <div className="form-body">
        {GROUPS.map((g) => (
          <fieldset key={g.title} className="form-grid pf-two">
            <legend className="pf-legend">{t(g.title)}</legend>
            {g.fields.map((f) => (
              <label key={f.key} className="lbl" htmlFor={`${base}-${f.key}`}>
                {t(`quote.field.${f.key}` as MessageId)}
                <input
                  id={`${base}-${f.key}`}
                  className="input mono"
                  inputMode="decimal"
                  value={shown(f.key, f.pct)}
                  onChange={(e) => set(f.key, e.target.value, f.pct)}
                  onBlur={() => done(f.key)}
                />
              </label>
            ))}
          </fieldset>
        ))}
        <fieldset className="form-grid pf-two">
          <legend className="pf-legend">{t('quote.group.terms')}</legend>
          {(['sso', 'scim'] as const).map((key) => (
            <label key={key} className="pf-flag">
              <input
                type="checkbox"
                checked={value[key]}
                onChange={(e) => onChange({ ...value, [key]: e.target.checked })}
              />
              <span>{t(`quote.field.${key}`)}</span>
            </label>
          ))}
          {select('sla', [
            { v: 'none', label: 'quote.sla.none' },
            { v: '99.9', label: 'quote.sla.999' },
            { v: '99.95', label: 'quote.sla.9995' },
          ])}
          {select('term_years', [
            { v: 1, label: 'quote.term.1' },
            { v: 2, label: 'quote.term.2' },
            { v: 3, label: 'quote.term.3' },
          ])}
          {select('payment', [
            { v: 'invoice', label: 'quote.payment.invoice' },
            { v: 'card', label: 'quote.payment.card' },
          ])}
          {select('currency', [
            { v: 'usd', label: 'quote.currency.usd' },
            { v: 'brl', label: 'quote.currency.brl' },
          ])}
        </fieldset>
      </div>
    </form>
  )
}

/** The prices and how each was computed, as key-value pairs (the recommended one in 600). */
function Result({ computed, input }: { computed: QuoteComputed; input: QuoteInput }) {
  const { t } = useI18n()
  const format = useFormat()
  const { usd, pct, n } = format
  const r = computed.result
  const brl = (v: number | null) => (v === null ? '–' : format.brl(v))
  const price = (p: (typeof r)['list'], strong = false, brlValue?: number | null): Row => ({
    value: (
      <>
        <span style={strong ? { fontWeight: 600 } : undefined}>
          {p.monthly === null ? t('quote.impossible') : usd(p.monthly, 0)}
        </span>
        {r.brl && brlValue !== undefined ? ` · ${brl(brlValue)}` : null}
      </>
    ),
    how: t('quote.how.price', {
      cost: usd(p.cost, 0),
      adds: usd(p.adds, 0),
      margin: pct(p.margin),
      shares: pct(r.shares.total),
    }),
  })
  type Row = { value: ReactNode; how: string }
  const rows: [MessageId, Row][] = [
    ['quote.walkAway', price(r.walk_away, false, r.brl?.walk_away)],
    ['quote.floor', price(r.floor, false, r.brl?.floor)],
    ['quote.list', price(r.list, true, r.brl?.list)],
    [
      'quote.pro',
      {
        value: usd(r.pro.monthly, 0),
        how: r.pro.list_above_pro
          ? t('quote.how.proAbove', {
              sso: usd(r.extras.sso, 0),
              scim: usd(r.extras.scim, 0),
              sla: usd(r.extras.sla, 0),
              support: usd(r.extras.support, 0),
              onboarding: usd(r.extras.onboarding, 0),
            })
          : r.pro.discount === null
            ? t('quote.how.pro', { captured: n(input.captured_per_month) })
            : t('quote.how.discount', { discount: pct(r.pro.discount) }),
      },
    ],
    [
      'quote.cost',
      {
        value: usd(r.now.total, 0),
        how: t('quote.how.cost', {
          variable: usd(r.now.variable, 0),
          summaries: usd(r.now.summaries, 0),
          crashes: usd(r.now.crashes, 0),
          yearEnd: usd(r.year_end.total, 0),
          captured: n(r.year_end.captured),
        }),
      },
    ],
    [
      'quote.adds',
      {
        value: usd(r.adds_year_end.total, 0),
        how: t('quote.how.adds', {
          capacity: usd(r.adds_year_end.capacity, 0),
          connections: usd(r.adds_year_end.connections, 0),
          support: usd(r.adds_year_end.support, 0),
          onboarding: usd(r.adds_year_end.onboarding, 0),
        }),
      },
    ],
    [
      'quote.shares',
      {
        value: pct(r.shares.total),
        how: t('quote.how.shares', {
          fee: pct(r.shares.fee),
          tax: pct(r.shares.tax),
          sla: pct(r.shares.sla),
          overhead: pct(r.shares.overhead),
        }),
      },
    ],
    [
      'quote.per1k',
      {
        value: r.per1k.price === null ? '–' : usd(r.per1k.price, 3),
        how: t('quote.how.per1k', { cost: usd(r.per1k.cost, 3) }),
      },
    ],
    [
      'quote.sensitivity',
      {
        value: [r.sensitivity.replay2x, r.sensitivity.session2x, r.sensitivity.volume150]
          .map((m) => (m === null ? '–' : pct(m)))
          .join(' · '),
        how: t('quote.how.sensitivity'),
      },
    ],
  ]
  return (
    <dl className="kv pf-how">
      {rows.map(([label, row]) => (
        <Fragment key={label}>
          <dt>{t(label)}</dt>
          <dd>{row.value}</dd>
          <dd className="how">{row.how}</dd>
        </Fragment>
      ))}
    </dl>
  )
}

/** Saves the quote with a name, a note and the reason (audited), in its form window. */
function Save({
  input,
  orgId,
  name0,
  onDone,
}: {
  input: QuoteInput
  orgId: string | null
  name0: string
  onDone: () => void
}) {
  const { t } = useI18n()
  const client = useQueryClient()
  const navigate = useNavigate()
  const [name, setName] = useState(name0)
  const [note, setNote] = useState('')
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<unknown>()
  useEffect(() => setName(name0), [name0])
  const ids = { name: useId(), note: useId() }
  const submit = async (e: FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError(undefined)
    try {
      const { id } = await saveQuote({ orgId, name, note, inputs: input, reason })
      await Promise.all([
        client.invalidateQueries({ queryKey: ['quotes'] }),
        client.invalidateQueries({ queryKey: ['audit'] }),
      ])
      setReason('')
      onDone()
      await navigate({ to: '/quote', search: { quote: id } })
    } catch (err) {
      setError(err)
    } finally {
      setBusy(false)
    }
  }
  return (
    <FormWindow
      title={t('quote.save')}
      submitLabel={t('quote.saveButton')}
      busy={busy}
      onSubmit={submit}
      onCancel={onDone}
      alert={error ? <ErrorState error={error} /> : null}
    >
      <div className="field-d">
        <label className="lbl" htmlFor={ids.name}>
          {t('quote.name')}
        </label>
        <input
          id={ids.name}
          className="input"
          required
          maxLength={200}
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
      </div>
      <div className="field-d">
        <label className="lbl" htmlFor={ids.note}>
          {t('quote.note')}
        </label>
        <input
          id={ids.note}
          className="input"
          maxLength={500}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </div>
      <ReasonField value={reason} onChange={setReason} hint={t('quote.saveMeta')} />
    </FormWindow>
  )
}

function Saved() {
  const { t, date } = useI18n()
  const q = useQuery(quotesQuery())
  if (q.isPending) return <Loading />
  if (q.isError) return <ErrorState error={q.error} retry={() => q.refetch()} />
  if (q.data.length === 0) return <Empty title={t('quote.noneSaved')} />
  return (
    <DataTable label={t('table.of', { name: t('quote.saved') })} minWidth={520}>
      <thead>
        <tr>
          <th className="f" scope="col">
            {t('quote.name')}
          </th>
          <th scope="col">{t('quote.note')}</th>
          <th className="last" scope="col">
            {t('quote.when')}
          </th>
        </tr>
      </thead>
      <tbody>
        {q.data.map((x) => (
          <tr key={x.id}>
            <td className="f wrap">
              <Link to="/quote" search={{ quote: x.id }} className="ul">
                {x.name}
              </Link>
            </td>
            <td className="wrap">{x.note}</td>
            <td className="last">{date(x.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </DataTable>
  )
}

export function QuotePage() {
  const { t } = useI18n()
  const search = useSearch({ strict: false }) as { org?: string; quote?: string }
  const fromOrg = useQuery({ ...planFitQuery(search.org ?? ''), enabled: !!search.org })
  const saved = useQuery({ ...quoteQuery(search.quote ?? ''), enabled: !!search.quote })
  const [input, setInput] = useState<QuoteInput>(QUOTE_DEFAULTS)
  const [seed, setSeed] = useState(0)
  const [saving, setSaving] = useState(false)
  // Pre-filled from an organization or a saved quote, once each loads.
  useEffect(() => {
    if (fromOrg.data) {
      setInput(inputsFrom(fromOrg.data))
      setSeed((s) => s + 1)
    }
  }, [fromOrg.data])
  useEffect(() => {
    if (saved.data) {
      setInput(saved.data.inputs)
      setSeed((s) => s + 1)
    }
  }, [saved.data])
  const computed = useQuery({
    queryKey: ['quote-compute', input],
    queryFn: () => computeQuote(input),
    placeholderData: keepPreviousData,
  })
  const s = saved.data
  const name0 = s?.name ?? fromOrg.data?.name ?? ''
  const orgId = s?.org_id ?? search.org ?? null
  const shown =
    s && JSON.stringify(s.inputs) === JSON.stringify(input) ? (s.current ?? s.saved) : computed.data
  return (
    <main id="main" className="pg">
      <PageHead
        title={t('quote.title')}
        meta={
          s
            ? t('quote.metaSaved', { name: s.name, version: s.saved.version })
            : fromOrg.data
              ? t('quote.metaOrg', { name: fromOrg.data.name })
              : t('quote.metaBlank')
        }
        actions={
          saving ? null : (
            <button type="button" className="btn sm" onClick={() => setSaving(true)}>
              {t('quote.save')}
            </button>
          )
        }
      />
      {saving ? (
        <Save input={input} orgId={orgId} name0={name0} onDone={() => setSaving(false)} />
      ) : null}
      <MoneyWarns
        items={
          s?.current
            ? [
                {
                  s: 'miss',
                  text: t('quote.modelChanged', {
                    saved: s.saved.version,
                    current: s.current.version,
                  }),
                },
              ]
            : []
        }
      />
      <div className="grid2 pf-half">
        <Inputs key={seed} value={input} onChange={setInput} />
        <Section
          title={t('quote.result')}
          meta={shown ? t('quote.resultMeta', { version: shown.version }) : undefined}
        >
          {computed.isError && !shown ? (
            <ErrorState error={computed.error} retry={() => computed.refetch()} />
          ) : shown ? (
            <>
              <Result computed={shown} input={input} />
              {s?.current && JSON.stringify(s.inputs) === JSON.stringify(input) ? (
                <>
                  <h3 className="pf-sub">{t('quote.asSaved', { version: s.saved.version })}</h3>
                  <Result computed={s.saved} input={input} />
                </>
              ) : null}
            </>
          ) : (
            <Loading />
          )}
        </Section>
      </div>
      <Section title={t('quote.saved')}>
        <Saved />
      </Section>
    </main>
  )
}
